/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import React, { Suspense, useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";

// Components.
import { ColumnTable, RowTable } from "@/components/table/UITableProps";
import UIButton from "@/components/buttons/UIButton";
import UIPrivateLayout from "@/components/layouts/UIPrivateLayout";
import UIRightSidebarLayout from "@/components/layouts/sidebar/UIRightSidebarLayout";
import UITable from "@/components/table/UITable";
import { Box, Grid, Paper, InputBase, IconButton } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";

import colors from "@/utils/colorsV2";

// Hooks
import { useProcessesByAgent } from "@/hooks/useProcessesByAgent";
import { useRequest } from "@/hooks/useRequest";
import { useInfoFiadoContext } from "@/context/infoFiadoContext";
import { useProcessStatus } from "@/hooks/useProcessStatus";
import UIModal from "@/components/modal/UIModal";
import { enqueueSnackbar } from "notistack";
import { useCurrentSessionContext } from "@/context/currentSessionContext";
import Breadcrumbs from "@/components/breadcrumbs/Breadcrumbs";
import UIText from "@/components/text/UIText";
import UILoading from "@/components/loading/UILoading";
import { Action } from "@/components/menu/menu-actions/UIMenuActions";
import Status, { estados, isContinueInPuntoAserta, isSignatureFadStatus, isSignatureStatus, statusFAD, statusManualSigned } from "@/utils/processStatus";
import UIContentModal from "@/components/modal/content-mis-solicitudes/UIContentModal";
import { useComparisionManagement } from "@/hooks/useComparisonManagement";
import { useModal } from "mui-modal-provider";
import UIDuplicateRegistrationModal from "@/components/dialogs/UIDuplicateRegistrationModal";
import { CompanyName, isAserta } from "@/utils/companies";
import { isManualSigned, isNumber } from "@/utils/functions";
import UIChip from "@/components/chips/UIChip";
import UIRadioButton from "@/components/inputs/radio-group/UIRadioButton";
import UISelect from "@/components/inputs/select/UISelect";
import UILoadingOverlay from "@/components/overlay/UILoadingOverlayComponent";
import { useGetAgents } from "@/hooks/useGetAgent";
import { useGetPromotedAgents } from "@/hooks/useGetPromotedAgents";
import { isRoleSucPromotorOrAgent, isSucursal, SPECIAL_USERS } from "@/utils/roles";
import FilterSidebar from "@/components/filteredSideBar/UIFilterSidebarSolicitudes";
import { useRequestControl } from "@/hooks/useRequestControl";
import UISelectSearch from "@/components/inputs/select/UISelectSearch";
import Icon from "@/components/icon/icon";
import { IconNames } from "@/components/icon/icon.props";
import useSignatureComparison from "@/hooks/create/useSignatureComparison";

// Constantes de paginación
const INITIAL_PAGE_SIZE = 100;
const BACKGROUND_PAGE_SIZE = 100;

const columnsTable: ColumnTable[] = [
  { id: "idSolicitud", label: "ID solicitud ASI" },
  { id: "nombre", label: "Cliente" },
  { id: "rfc", label: "RFC" },
  { id: "estatus", label: "Estatus solicitud" },
  { id: "fechaAlta", label: "Fecha de inicio de alta" },
  { id: "empresa", label: "Aseguradora" },
  { id: "agenteCorredor", label: "Agente" },
  { id: "nombreUsuario", label: "Responsable de la solicitud" },
  { id: "actions", label: "Acciones" },
];

const breadcrumbsOptions = [
  {
    icon: <HomeIcon sx={{ fontSize: "20px", color: "#566680" }} />,
  },
  {
    label: "Altas en proceso",
  },
];

const statusByAction = {
  edit: [Status.VERIFICATION],
  view: [
    Status.IN_SIGNING,
    Status.CONTRACT_REVIEW,
    Status.GUARANTOR_CONTRACT_REVIEW,
    Status.CONTRACT_VALIDATION_REJECT,
  ],
  duplicate: [Status.REGISTRATION_COMPLETE],
  cancel: [
    Status.DUPLICATE_GUARANTOR,
    Status.INVALID_GUARANTOR,
    Status.REGISTRATION_COMPLETE,
    Status.ERROR_LEGACY,
  ],
  retry: [
    Status.GUARANTOR_VALIDATION_ERROR,
    Status.CREATING_REQUEST_ERROR,
    Status.ERROR_GUARANTOR_CONTRACT_REVIEW,
    Status.ERROR_IN_SIGNING,
    Status.UPDATING_REQUEST_ERROR,
    Status.ERROR_AGENT_CHANGE_REQUEST,
    Status.ERROR_GUARANTOR_CHANGE_REQUEST,
  ],
  siebel: [
    Status.IN_SIGNING,
    Status.CONTRACT_REVIEW,
    Status.GUARANTOR_CONTRACT_REVIEW,
    Status.CONTRACT_VALIDATION_REJECT,
    Status.REGISTRATION_COMPLETE,
    Status.CONTINUE_SIEBEL,
  ],
  block: [
    Status.VERIFICATION,
    Status.CONTRACT_REVIEW,
    Status.CONTRACT_VALIDATION_REJECT,
    Status.ERROR_CORRECTING_SIGNATURE
  ]
};

interface ModalProperties {
  open: boolean;
  title: string;
  type: 'view' | 'duplicate' | 'cancel';
  isFad?: boolean;
  idSolicitud: string;
}

interface FilterOptions {
  aseguradora: string;
  sucursal: string;
  agenteId: string[];
  searchTerm: string;
  ordenarPor: string;
  estatus: {
    solicitudCompleta: boolean;
    enProceso: boolean;
    enProcesoFirma: boolean;
    errorEnSistema: boolean;
  };
}

function Home() {
  const { showModal, hideModal } = useModal();
  const { data, setData, idProcessFiado, setIdProcessFiado } = useInfoFiadoContext();
  const [isLoading, setIsLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState("Cargando solicitudes...");

  // Estados de paginación y filtros
  const [allRequests, setAllRequests] = useState<RowTable[]>([]);
  const [filteredData, setFilteredData] = useState<RowTable[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [clientData, setClientData] = useState<RowTable[]>([]);

  const [paginationInfo, setPaginationInfo] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 100,
  });

  const [loadingCancelToken, setLoadingCancelToken] = useState<AbortController | null>(null);
  const [currentLoadingPromise, setCurrentLoadingPromise] = useState<Promise<void> | null>(null);

  const { session } = useCurrentSessionContext();
  const [agent, setAgent] = useState<string>(session?.user?.login ?? "");
  const [company, setCompany] = useState<string>("");
  const [next, setNext] = useState<boolean>(false);

  const [modalProperties, setModalProperties] = useState<ModalProperties>({
    open: false,
    title: "",
    type: "view",
    idSolicitud: ""
  });

  // Filtros aplicados y temporales
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({
    aseguradora: '',
    sucursal: '',
    agenteId: [],
    searchTerm: '',
    ordenarPor: '',
    estatus: {
      solicitudCompleta: false,
      enProceso: false,
      enProcesoFirma: false,
      errorEnSistema: false,
    }
  });

  const [tempFilters, setTempFilters] = useState<FilterOptions>(appliedFilters);

  const { push } = useRouter();
  const searchParams = useSearchParams();
  const { retryComparison, cancelComparison, siebelComparison } = useComparisionManagement();
  const { getInfoFiado } = useRequest();
  const processByAgent = useProcessesByAgent();
  const { sendToFAD, changeToManualSign } = useSignatureComparison();
  const getProcessStatus = useProcessStatus();

  // Refs para acceder a valores actualizados
  const processByAgentRef = useRef(processByAgent);
  const appliedFiltersRef = useRef<FilterOptions>(appliedFilters);
  const isFetchingAgentsRef = useRef(false);

  // Estados para carga de agentes
  const [isLoadingAgents, setIsLoadingAgents] = useState<boolean>(true);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [agents, setAgents] = useState<Array<{ id: string, value: string, name: string, agentType: string }>>([]);
  const [selected, setSelected] = useState<string[]>([]);

  // Servicios para obtener agentes
  const getAgentsService = useGetAgents();
  const getPromotedAgentsService = useGetPromotedAgents();

  // Determinar si es usuario especial
  const isSpecialUser = useMemo(() =>
    SPECIAL_USERS.includes(session?.user?.login?.toUpperCase() || ''),
    [session?.user?.login]
  );

  // Actualizar refs
  useEffect(() => {
    processByAgentRef.current = processByAgent;
  }, [processByAgent]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  // Compañías disponibles
  const companies = useMemo(() =>
    session?.company?.map((item) => ({
      id: item.name,
      value: item.name
    })) ?? [],
    [session]
  );

  // Sucursales disponibles
  const sucursales = useMemo(() => {
    if (!tempFilters.aseguradora) return [];
    const companySucursales = session?.company?.find(c => c.name === tempFilters.aseguradora)?.subsidiary.map((sub) => ({
      id: sub.subsidiaryId,
      value: `${sub.subsidiary} - ${sub.agentPositionName}`
    })) ?? [];

    if (companySucursales.length === 1) {
      return companySucursales;
    }
    return [{ id: '', value: 'Selecciona una sucursal' }, ...companySucursales];
  }, [session, tempFilters.aseguradora]);

  // Verificar si hay filtros activos
  const hasActiveFilters = useMemo(() =>
    tempFilters.aseguradora !== '' ||
    tempFilters.sucursal !== '' ||
    tempFilters.agenteId.length > 0 ||
    tempFilters.searchTerm !== '' ||
    tempFilters.ordenarPor !== '' ||
    Object.values(tempFilters.estatus).some(v => v),
    [tempFilters]
  );

  useEffect(() => {
    const urlSearch = searchParams.get("search");
    if (urlSearch) {
      setTempFilters(prev => ({ ...prev, searchTerm: urlSearch }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (data !== undefined && next) {
      stepInProcess(idProcessFiado);
    }
  }, [data, next]);

  useEffect(() => {
    if (!session) return;
    if (session.user?.login === undefined || session.user?.login === '') return;
    if (agent === session.user.login) return;
    setAgent(session.user.login);
  }, [session]);

  const cancelCurrentLoading = useCallback(() => {
    if (loadingCancelToken && !loadingCancelToken.signal.aborted) {
      loadingCancelToken.abort();
      setLoadingCancelToken(null);
    }
    setCurrentLoadingPromise(null);
  }, [loadingCancelToken]);

  const fetchAgents = useCallback(async () => {
    if (isFetchingAgentsRef.current) {
      return;
    }

    // Para usuarios especiales o normales sin filtros, no hacer nada
    if (!tempFilters.aseguradora || !tempFilters.sucursal) {
      setIsLoadingAgents(false);
      return;
    }

    const role = session?.user?.role?.toUpperCase();
    const service = isSucursal(role) ? getAgentsService : getPromotedAgentsService;

    try {
      isFetchingAgentsRef.current = true;
      const newFilters = { ...tempFilters };

      if (isRoleSucPromotorOrAgent(session)) {
        setIsLoadingAgents(true);
        const subsidiary = session?.company?.find(c => c.name === tempFilters.aseguradora)?.subsidiary
          .find(s => `${s.subsidiary} - ${s.agentPositionName}` === tempFilters.sucursal);

        if (!subsidiary) throw new Error("Subsidiary not found");

        const response = await service({
          idSucursal: subsidiary.subsidiaryId,
          empresa: tempFilters.aseguradora,
          idPromotor: subsidiary.corredorId
        });

        const agentOptions = response.map(agent => ({
          id: agent.id ?? "",
          value: `${agent.numAgente} - ${agent.nombreAgente}`,
          name: agent.nombreAgente,
          agentType: agent.tipoPersona
        }));

        setAgents(agentOptions);

        const selectNewAgent = agentOptions.find(agent => tempFilters.agenteId.includes(agent.id));

        if (!selectNewAgent && agentOptions.length > 0 && !isSpecialUser) {
          // Solo autoseleccionar para usuarios normales
          const firstAgent = agentOptions[0];
          setSelected([firstAgent.id]);
          newFilters.agenteId = [firstAgent.id];
          setTempFilters(newFilters);
        } else {
          setSelected(selectNewAgent ? [selectNewAgent.id] : []);
        }
      }

      // Solo cargar automáticamente para usuarios normales en el primer load
      if (isFirstLoad && !isSpecialUser) {
        setIsFirstLoad(false);
        setAppliedFilters(newFilters);
        await getList(agent, newFilters);
      } else if (isFirstLoad) {
        setIsFirstLoad(false);
      }

      return tempFilters.agenteId;
    } catch (error) {
      console.error("Error fetching agents:", error);
      enqueueSnackbar("Error al cargar los agentes", { variant: "error" });
      return null;
    } finally {
      setIsLoadingAgents(false);
      isFetchingAgentsRef.current = false;
    }
  }, [tempFilters.aseguradora, tempFilters.sucursal, session, agent, isSpecialUser]);

  // Filtrado optimizado solo en campos relevantes
  const applyFilters = useCallback((data: RowTable[], filters: FilterOptions) => {
    let filtered = [...data];

    // Ordenamiento
    if (filters.ordenarPor) {
      filtered.sort((a, b) => {
        const dateA = new Date(a.fechaAlta);
        const dateB = new Date(b.fechaAlta);
        if (filters.ordenarPor === 'antiguo') {
          return dateA.getTime() - dateB.getTime();
        } else if (filters.ordenarPor === 'reciente') {
          return dateB.getTime() - dateA.getTime();
        }
        return 0;
      });
    } else {
      filtered.sort((a, b) => {
        const dateA = new Date(a.fechaAlta);
        const dateB = new Date(b.fechaAlta);
        return dateB.getTime() - dateA.getTime();
      });
    }


    // Filtro por agente solo para usuarios especiales
    if (isSpecialUser && filters.agenteId.length > 0) {
      filtered = filtered.filter(item => item.agenteCorredorId === filters.agenteId[0]);
    }

    // Filtro por búsqueda solo en campos específicos
    if (filters.searchTerm) {
      const lowercasedSearch = filters.searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const searchableFields = [
          item.idSolicitud,
          item.nombre,
          item.nombreUsuario,
          item.rfc,
          item.estatus
        ].filter(Boolean);

        return searchableFields.some(value =>
          String(value).toLowerCase().includes(lowercasedSearch)
        );
      });
    }

    // Filtro por aseguradora
    if (filters.aseguradora) {
      filtered = filtered.filter(item =>
        item.empresa?.toString().toLowerCase() === filters.aseguradora.toLowerCase()
        || item.empresa?.toLocaleString().toLowerCase() === "ambas"
      );
    }

    // Filtro por estatus
    const activeStatusFilters = Object.entries(filters.estatus).filter(([_, value]) => value);
    if (activeStatusFilters.length > 0) {
      filtered = filtered.filter(item => {
        const itemStatusId = item.idEstatus?.toString() || '';

        return activeStatusFilters.some(([key, _]) => {
          switch (key) {
            case 'solicitudCompleta':
              return itemStatusId === Status.REGISTRATION_COMPLETE;
            case 'enProceso':
              return itemStatusId === Status.VERIFICATION || 
                itemStatusId === Status.CONTRACT_REVIEW;
            case 'enProcesoFirma':
              return itemStatusId === Status.IN_SIGNING ||
                // itemStatusId === Status.CONTRACT_REVIEW ||
                itemStatusId === Status.GUARANTOR_CONTRACT_REVIEW;
            case 'errorEnSistema':
              return itemStatusId === Status.ERROR_EXTRACT_DATA ||
                itemStatusId === Status.GUARANTOR_VALIDATION_ERROR ||
                itemStatusId === Status.CREATING_REQUEST_ERROR ||
                itemStatusId === Status.UPDATING_REQUEST_ERROR ||
                itemStatusId === Status.ERROR_LEGACY ||
                itemStatusId === Status.ERROR_GUARANTOR_CONTRACT_REVIEW ||
                itemStatusId === Status.ERROR_IN_SIGNING ||
                itemStatusId === Status.ERROR_AGENT_CHANGE_REQUEST ||
                itemStatusId === Status.ERROR_GUARANTOR_CHANGE_REQUEST;
            default:
              return false;
          }
        });
      });
    }

    setFilteredData(filtered);
  }, []);

  // Carga de páginas en background sin dependencias para evitar re-creación
  const loadRemainingPages = useCallback(async (
    agentId: string,
    filters: FilterOptions,
    totalPages: number,
    abortSignal: AbortSignal
  ) => {
    let currentPage = 1;
    setIsLoadingMore(true);

    const loadingPromise = (async () => {
      try {
        while (currentPage <= totalPages) {
          if (abortSignal.aborted) break;

          const pageRequests = [
            processByAgent({
              id: agentId,
              page: currentPage,
              size: BACKGROUND_PAGE_SIZE,
              agentes: filters.agenteId
            }, abortSignal),
            currentPage + 1 < totalPages ? processByAgent({
              id: agentId,
              page: currentPage + 1,
              size: BACKGROUND_PAGE_SIZE,
              agentes: filters.agenteId
            }, abortSignal) : null
          ].filter(Boolean);

          const pageResponses = await Promise.all(pageRequests);

          const newData = pageResponses.flatMap(response => {
            if (response && response.contenido) {
              return response.contenido.map(item => {
                if (!item.idEstatus) {
                  item.idEstatus = item.estatus;
                }
                item.estatus = estados[item.idEstatus]?.message ?? item.estatus;
                item.agenteCorredor = item.agenteCorredorId ? `${item.agenteCorredorId}${getNameAgente(item.agenteCorredorId as string)}` : '';
                item.bloqueo = item.bloqueo ? `${item.bloqueo}` : `false`;
                item.nombreUsuario = item.bloqueo === 'true' ? item.nombreUsuario : "---";
                return item;
              });
            }
            return [];
          });

          await new Promise<void>(resolve => {
            setAllRequests(prevData => {
              const updatedData = [...prevData, ...newData.filter(
                newItem => !prevData.some(item => item.idSolicitud === newItem.idSolicitud))];

              setClientData(updatedData);

              // Usar filtros del ref que siempre están actualizados
              const currentFilters = appliedFiltersRef.current;

              // Aplicar filtros inline para evitar dependencias
              let filtered = [...updatedData];

              if (currentFilters.ordenarPor) {
                filtered.sort((a, b) => {
                  const dateA = new Date(a.fechaAlta);
                  const dateB = new Date(b.fechaAlta);
                  if (currentFilters.ordenarPor === 'antiguo') {
                    return dateA.getTime() - dateB.getTime();
                  } else if (currentFilters.ordenarPor === 'reciente') {
                    return dateB.getTime() - dateA.getTime();
                  }
                  return 0;
                });
              } else {
                filtered.sort((a, b) => {
                  const dateA = new Date(a.fechaAlta);
                  const dateB = new Date(b.fechaAlta);
                  return dateB.getTime() - dateA.getTime();
                });
              }

              if (isSpecialUser && currentFilters.agenteId.length > 0) {
                filtered = filtered.filter(item => item.agenteCorredorId === currentFilters.agenteId[0]);
              }

              if (currentFilters.searchTerm) {
                const lowercasedSearch = currentFilters.searchTerm.toLowerCase();
                filtered = filtered.filter((item) => {
                  const searchableFields = [
                    item.agenteCorredor,
                    item.idSolicitud,
                    item.nombre,
                    item.nombreUsuario,
                    item.rfc,
                    item.estatus
                  ].filter(Boolean);

                  return searchableFields.some(value =>
                    String(value).toLowerCase().includes(lowercasedSearch)
                  );
                });
              }

              if (currentFilters.aseguradora) {
                filtered = filtered.filter(item =>
                  item.empresa?.toString().toLowerCase() === currentFilters.aseguradora.toLowerCase()
                  || item.empresa?.toLocaleString().toLowerCase() === "ambas"
                );
              }

              const activeStatusFilters = Object.entries(currentFilters.estatus).filter(([_, value]) => value);
              if (activeStatusFilters.length > 0) {
                filtered = filtered.filter(item => {
                  const itemStatusId = item.idEstatus?.toString() || '';

                  return activeStatusFilters.some(([key, _]) => {
                    switch (key) {
                      case 'solicitudCompleta':
                        return itemStatusId === Status.REGISTRATION_COMPLETE;
                      case 'enProceso':
                        return itemStatusId === Status.VERIFICATION;
                      case 'enProcesoFirma':
                        return itemStatusId === Status.IN_SIGNING ||
                          itemStatusId === Status.CONTRACT_REVIEW ||
                          itemStatusId === Status.GUARANTOR_CONTRACT_REVIEW;
                      case 'errorEnSistema':
                        return itemStatusId === Status.ERROR_EXTRACT_DATA ||
                          itemStatusId === Status.GUARANTOR_VALIDATION_ERROR ||
                          itemStatusId === Status.CREATING_REQUEST_ERROR ||
                          itemStatusId === Status.UPDATING_REQUEST_ERROR ||
                          itemStatusId === Status.ERROR_LEGACY ||
                          itemStatusId === Status.ERROR_GUARANTOR_CONTRACT_REVIEW ||
                          itemStatusId === Status.ERROR_IN_SIGNING ||
                          itemStatusId === Status.ERROR_AGENT_CHANGE_REQUEST ||
                          itemStatusId === Status.ERROR_GUARANTOR_CHANGE_REQUEST;
                      default:
                        return false;
                    }
                  });
                });
              }

              setFilteredData(filtered);

              resolve();
              return updatedData;
            });
          });

          currentPage += pageRequests.length;
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error('Error en loadRemainingPages:', error);
      } finally {
        if (!abortSignal.aborted) {
          setIsLoadingMore(false);
        }
      }
    })();

    setCurrentLoadingPromise(loadingPromise);
    await loadingPromise;
  }, []);

  const getList = useCallback(async (agente: string, filters: FilterOptions) => {
    if (loadingCancelToken && !loadingCancelToken.signal.aborted) {
      loadingCancelToken.abort();
      setLoadingCancelToken(null);
    }

    if (currentLoadingPromise) {
      await currentLoadingPromise;
      setCurrentLoadingPromise(null);
    }

    const abortController = new AbortController();
    const signal = abortController.signal;
    setLoadingCancelToken(abortController);

    setIsLoading(true);
    setMessageLoading("Buscando solicitudes...");

    try {
      const currentProcessByAgent = processByAgentRef.current;

      const firstPageResponse = await currentProcessByAgent({
        id: agente,
        page: 0,
        size: INITIAL_PAGE_SIZE,
        agentes: filters.agenteId
      }, signal);

      if (firstPageResponse && firstPageResponse.contenido) {
        const processedData = firstPageResponse.contenido.map(item => {
          if (!item.idEstatus) {
            item.idEstatus = item.estatus;
          }
          item.estatus = estados[item.idEstatus]?.message ?? item.estatus;
          item.agenteCorredor = item.agenteCorredorId ? `${item.agenteCorredorId}${getNameAgente(item.agenteCorredorId as string)}` : '';
          item.bloqueo = item.bloqueo ? `${item.bloqueo}` : `false`;
          item.nombreUsuario = item.bloqueo === 'true' ? item.nombreUsuario : "---";
          return item;
        });

        setPaginationInfo({
          totalRecords: firstPageResponse.elementosTotales,
          totalPages: firstPageResponse.paginasTotales,
          currentPage: firstPageResponse.paginaActual,
          pageSize: firstPageResponse.tamanioPagina,
        });

        setAllRequests(processedData);
        setClientData(processedData);
        applyFilters(processedData, filters);

        if (firstPageResponse.paginasTotales > 1) {
          loadRemainingPages(agente, filters, firstPageResponse.paginasTotales, signal);
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error("Error en getList:", error);
        enqueueSnackbar(error.message || "Error al cargar solicitudes", {
          variant: "error",
          preventDuplicate: true,
        });
      }
    } finally {
      setIsLoading(false);
    }

    return () => {
      abortController.abort();
    };
  }, [applyFilters, loadRemainingPages]);

  const agentsRef = useRef<Array<{ id: string, value: string, name: string, agentType: string }>>([]);

  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);

  const getNameAgente = useCallback((id: string) => {
    const agent = agentsRef.current.find(a => a.id === id);
    return agent ? ` - ${agent.name}` : '';
  }, []);

  // ✅ useEffect de inicialización con carga automática para usuarios especiales
  useEffect(() => {
    if (!agent) return;

    if (companies.length > 0 && !tempFilters.aseguradora && isFirstLoad) {
      if (isSpecialUser) {
        // Para usuarios especiales, inicializar vacío Y cargar automáticamente
        const emptyFilters: FilterOptions = {
          aseguradora: '',
          sucursal: '',
          agenteId: [],
          searchTerm: '',
          ordenarPor: '',
          estatus: {
            solicitudCompleta: false,
            enProceso: false,
            enProcesoFirma: false,
            errorEnSistema: false,
          }
        };
        setAppliedFilters(emptyFilters);
        setTempFilters(emptyFilters);
        setIsFirstLoad(false);
        setIsLoadingAgents(false);

        // ✅ Cargar automáticamente sin filtros
        getList(agent, emptyFilters);
        return;
      }

      // Para usuarios normales, inicializar con valores por defecto
      const firstCompany = companies.find(c => c.value.toUpperCase() === "ASERTA") || companies[0];
      const initialFilters: FilterOptions = {
        ...tempFilters,
        aseguradora: firstCompany.value,
      };

      if (session?.company) {
        const asertaCompany = session.company.find(c => c.name === firstCompany.value);
        if (asertaCompany && asertaCompany.subsidiary.length > 0) {
          const initialSubsidiary = asertaCompany.subsidiary.filter(s => s.isDefault)[0] ?? asertaCompany.subsidiary[0];
          initialFilters.sucursal = `${initialSubsidiary.subsidiary} - ${initialSubsidiary.agentPositionName}`;
          initialFilters.agenteId = initialSubsidiary.corredorId ? [initialSubsidiary.corredorId] : initialSubsidiary.agentId ? [initialSubsidiary.agentId] : ["NA"];
        }
      }

      setAppliedFilters(initialFilters);
      setTempFilters(initialFilters);
    }
  }, [agent, companies, isFirstLoad, session, isSpecialUser, getList]);

  useEffect(() => {
    if (tempFilters.sucursal && tempFilters.sucursal !== 'Selecciona una sucursal') {
      fetchAgents();
    }
  }, [tempFilters.sucursal, tempFilters.aseguradora]);

  useEffect(() => {
    return () => {
      if (loadingCancelToken && !loadingCancelToken.signal.aborted) {
        loadingCancelToken.abort();
      }
    };
  }, []);

  const stepInProcess = (id: string) => {
    push(`/create/tax-situation?idProcess=${id}&company=${company}`);
  };

  const handleCompanyChange = (value: string) => {
    const newFilters = { ...tempFilters, aseguradora: value, sucursal: '', agenteId: [] };
    const companySucursales = session?.company?.find(c => c.name === value)?.subsidiary ?? [];

    if (companySucursales.length > 0 && !isSpecialUser) {
      const onlySucursal = companySucursales.filter(s => s.isDefault)[0] ?? companySucursales[0];
      newFilters.sucursal = `${onlySucursal.subsidiary} - ${onlySucursal.agentPositionName}`;
    }

    setAgents([]);
    setSelected([]);
    setIsLoadingAgents(true);
    setTempFilters(newFilters);
  };

  const handleSucursalChange = (event: any) => {
    setAgents([]);
    setSelected([]);
    setIsLoadingAgents(true);
    setTempFilters(prev => ({ ...prev, sucursal: event, agenteId: [] }));
  };

  const handleAgentChange = (value: string, id: string | number) => {
    setSelected([id as string]);
    const newFilters = { ...tempFilters, agenteId: [id as string] };
    setTempFilters(newFilters);
  };

  const applyFiltersAndFetch = useCallback((newFilters: FilterOptions) => {
    cancelCurrentLoading();
    setAllRequests([]);
    setClientData([]);
    setFilteredData([]);
    setPaginationInfo(prev => ({
      ...prev,
      currentPage: 1,
      totalRecords: 0,
      totalPages: 0
    }));

    setAppliedFilters(newFilters);
    getList(agent, newFilters);
  }, [cancelCurrentLoading, agent, getList]);

  const handleFilterChange = useCallback((newFilters: FilterOptions) => {
    const requiresFetch =
      newFilters.aseguradora !== appliedFilters.aseguradora ||
      newFilters.sucursal !== appliedFilters.sucursal ||
      JSON.stringify(newFilters.agenteId) !== JSON.stringify(appliedFilters.agenteId);

    setTempFilters(newFilters);

    if (requiresFetch) {
      applyFiltersAndFetch(newFilters);
    } else {
      setAppliedFilters(newFilters);
      applyFilters(clientData, newFilters);
    }
  }, [appliedFilters, clientData, applyFilters, applyFiltersAndFetch]);

  const handleTempFilterChange = (key: keyof FilterOptions, value: any) => {
    const newFilters = {
      ...tempFilters,
      [key]: value
    };
    setTempFilters(newFilters);

    if (key === 'searchTerm') {
      setAppliedFilters(newFilters);
      applyFilters(clientData, newFilters);
    } else {
      handleFilterChange(newFilters);
    }
  };

  const toggleFilterSidebar = useCallback(() => {
    setIsFilterOpen(prev => !prev);
  }, []);

  const handleRefresh = useCallback(() => {
    setTempFilters(appliedFilters);
    getList(agent, appliedFilters);
  }, [appliedFilters, agent, getList]);

  const handleSearch = useCallback(() => {
    applyFiltersAndFetch(tempFilters);
  }, [applyFiltersAndFetch, tempFilters]);

  // Función para limpiar todos los filtros (solo usuarios especiales)
  const handleClearAllFilters = useCallback(() => {
    const emptyFilters: FilterOptions = {
      aseguradora: '',
      sucursal: '',
      agenteId: [],
      searchTerm: '',
      ordenarPor: '',
      estatus: {
        solicitudCompleta: false,
        enProceso: false,
        enProcesoFirma: false,
        errorEnSistema: false,
      }
    };

    setAgents([]);
    setSelected([]);
    setTempFilters(emptyFilters);
    setAppliedFilters(emptyFilters);

    applyFiltersAndFetch(emptyFilters);
  }, [applyFiltersAndFetch]);

  const useSearchButtonState = (
    isLoadingAgents: boolean,
    isFirstLoad: boolean,
    isRoleAgentOrPromotor: (session: any) => boolean,
    tempFilters: FilterOptions,
    agents: Array<{ id: string; value: string; name: string; agentType: string }>,
    isSpecialUser: boolean
  ) => {
    return useMemo(() => {
      if (isLoadingAgents || isFirstLoad) return true;

      // Para usuarios especiales, NUNCA deshabilitar
      if (isSpecialUser) return false;

      // Para usuarios normales, validar filtros
      if (isRoleAgentOrPromotor(session)) {
        return tempFilters.agenteId.length === 0 || agents.length === 0;
      }
      return false;
    }, [isLoadingAgents, isFirstLoad, isRoleAgentOrPromotor, tempFilters.agenteId, agents.length, isSpecialUser]);
  };

  const isSearchDisabled = useSearchButtonState(
    isLoadingAgents,
    isFirstLoad,
    isRoleSucPromotorOrAgent,
    tempFilters,
    agents,
    isSpecialUser
  );

  const viewHandler = async (solicitud: RowTable) => {
    setModalProperties({
      open: true,
      title: "",
      type: "view",
      isFad: !isManualSigned(solicitud.idEstatus),
      idSolicitud: solicitud.idSolicitud.toString()
    });
  };

  const editHandler = async (solicitud: RowTable) => {
    setIsLoading(true);
    setMessageLoading("Buscando solicitud...");
    setCompany(solicitud.empresa.toString());

    const id = solicitud.idSolicitud.toString();
    getInfoFiado(id)
      .then((response) => {
        setMessageLoading("Solicitud encontrada");
        setIdProcessFiado(id);
        setData(response);
        setNext(true);
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const retryHandler = async (solicitud: RowTable) => {
    setIsLoading(true);
    setMessageLoading("Reintentando acciones fallidas...");

    const id = solicitud.idSolicitud.toString();
    retryComparison({ idProceso: id })
      .then((response) => {
        setMessageLoading("Solicitud reenviada");
        getList(agent, appliedFilters);
      })
      .catch((error) => {
        enqueueSnackbar(error.message, {
          variant: "error",
          preventDuplicate: true,
        });
        setIsLoading(false);
      });
  };

  const duplicateHandler = async (solicitud: RowTable) => {
    const companyName = isAserta(solicitud.empresa?.toString()) ? CompanyName.INSURGENTES : CompanyName.ASERTA;
    showModal(
      UIDuplicateRegistrationModal,
      {
        onAccept: async () => {
          setMessageLoading("Actualizando lista de solicitudes...");
          await getList(agent, appliedFilters);
        },
        processId: solicitud.idSolicitud.toString(),
        companyName: companyName,
        setIsLoadingView: setIsLoading,
        setMessageLoadingView: setMessageLoading,
      }
    );
  };

  const cancelHandler = async (solicitud: RowTable) => {
    setModalProperties({
      open: true,
      title: "",
      type: "cancel",
      idSolicitud: solicitud.idSolicitud.toString()
    });
  };

  const siebelContinueHandler = async (solicitud: RowTable) => {
    setIsLoading(true);
    setMessageLoading("Actualizando solicitud...");

    const id = solicitud.idSolicitud.toString();
    siebelComparison({ idProcess: id })
      .then((response) => {
        if(response !== "OK"){
          throw response;
        }
        setMessageLoading("Solicitud actualizada");
        getList(agent, appliedFilters);
      })
      .catch((error) => {
        enqueueSnackbar(error.message, {
          variant: "error",
          preventDuplicate: true,
        });
        setIsLoading(false);
      });
  };

  const isValidStatus = (idStatus: string | number, statuses: string[]) => {
    if (!idStatus) return false;
    const id = idStatus.toString();
    if (!id || id === "") return false;
    return statuses.includes(id);
  };

  const isMine = (solicitud: RowTable) => {
    return solicitud.nombreUsuario === session?.user?.login;
  }

  const viewHistoryHandler = async (solicitud: RowTable) => {
    push(`/history?idProcess=${solicitud.idSolicitud}&type=REGISTRATION`);
  }

  // Función auxiliar para actualizar el estado de bloqueo de solicitudes
  const updateRequestLockStatus = useCallback((
    idSolicitud: string | number,
    bloqueo: "true" | "false",
    nombreUsuario: string
  ) => {
    const updateFn = (prevRequests: RowTable[]) => {
      return prevRequests.map(item => {
        if (item.idSolicitud === idSolicitud) {
          return {
            ...item,
            bloqueo,
            nombreUsuario
          };
        }
        return item;
      });
    };

    setAllRequests(updateFn);
    setFilteredData(updateFn);
  }, []);

  // Función auxiliar para actualizar el estado de firma de una solicitud
  const updateRequestSignatureStatus = useCallback((
    idSolicitud: string | number,
    newIdEstatus: Status,
    newEstatus?: string
  ) => {
    const updateFn = (prevRequests: RowTable[]) => {
      return prevRequests.map(item => {
        if (item.idSolicitud === idSolicitud) {
          return {
            ...item,
            idEstatus: newIdEstatus,
            estatus: newEstatus || estados[newIdEstatus]?.message || item.estatus
          };
        }
        return item;
      });
    };

    setAllRequests(updateFn);
    setFilteredData(updateFn);
    setClientData(updateFn);
  }, []);

  // Función de polling para verificar el cambio de estado en Camunda
  const pollForStatusChange = useCallback(async (
    idSolicitud: string,
    expectedStatuses: Status[],
    maxAttempts: number = 5,
    intervalMs: number = 2000
  ): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Esperar antes de consultar (excepto en el primer intento)
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        } else {
          // Primer intento: esperar 3 segundos para dar tiempo a Camunda
          await new Promise(resolve => setTimeout(resolve, 3000));
        }

        const statusResponse = await getProcessStatus({ idProceso: idSolicitud });

        if (statusResponse && expectedStatuses.includes(statusResponse.idEstatus as Status)) {
          // Estado encontrado, actualizar
          updateRequestSignatureStatus(
            idSolicitud,
            statusResponse.idEstatus as Status,
            statusResponse.estatus
          );
          return true;
        }
      } catch (error) {
        console.error(`Error en intento ${attempt + 1} de polling:`, error);
      }
    }

    // Si no se encontró el estado esperado después de todos los intentos, recargar lista
    return false;
  }, [getProcessStatus, updateRequestSignatureStatus]);

  // Hook de control de solicitudes
  const {
    getTakeControlAction,
    getReleaseControlAction,
  } = useRequestControl({
    validStatuses: statusByAction.block,
    session: session!,
    appliedFilters,
    agents,
    processType: 'REGISTRATION',

    onTakeControl: useCallback((solicitud: RowTable) => {
      // Actualizar estado de bloqueo
      updateRequestLockStatus(
        solicitud.idSolicitud,
        "true",
        session?.user?.login || ""
      );

      // Redirigir según el estado
      if (isValidStatus(solicitud.idEstatus, statusByAction.edit)) {
        editHandler(solicitud);
      } else if (isValidStatus(solicitud.idEstatus, statusByAction.view)) {
        viewHandler(solicitud);
      }
    }, [updateRequestLockStatus, session?.user?.login, editHandler, viewHandler]),

    onReleaseControl: useCallback((solicitud: RowTable) => {
      // Actualizar estado de bloqueo
      updateRequestLockStatus(
        solicitud.idSolicitud,
        "false",
        "---"
      );
    }, [updateRequestLockStatus]),

    setIsLoading,
    setMessageLoading
  });


  const actions: Action[] = [
    {
      label: "Ver solicitud",
      onClick: viewHandler,
      icon: "/assets/icons/document-view.svg",
      disabled: (solicitud: RowTable) => {
        try {
          return !isValidStatus((solicitud as RowTable).idEstatus, statusByAction.view) || !isMine(solicitud as RowTable);
        } catch (error) {
          return true;
        }
      }
    },
    {
      label: "Editar solicitud",
      onClick: editHandler,
      icon: "/assets/icons/edit.svg",
      disabled: (solicitud: RowTable) => {
        return !isValidStatus((solicitud as RowTable).idEstatus, statusByAction.edit) || !isMine(solicitud as RowTable);
      },
    },
    // AGREGAR LAS ACCIONES DEL HOOK
    getTakeControlAction(),
    getReleaseControlAction(),

    {
      label: "Reiniciar proceso fallido",
      onClick: retryHandler,
      icon: "/assets/icons/edit.svg",
      disabled: (solicitud: RowTable) => {
        return !isValidStatus(solicitud.idEstatus, statusByAction.retry) || !isMine(solicitud as RowTable);
      },
    },
    {
      label: "Replicar solicitud",
      onClick: duplicateHandler,
      icon: "/assets/icons/copy.svg",
      disabled: (solicitud: RowTable) => {
        return !isValidStatus(solicitud.idEstatus, statusByAction.duplicate);
      },
    },
    {
      label: (row: RowTable) => {
        if (statusFAD.includes(row.idEstatus as Status)) {
          return "Cambiar a firma manual";
        }

        return "Cambiar a firma digital";
      },
      icon: (row: RowTable) => {
        if (statusFAD.includes(row.idEstatus as Status)) {
          return "/assets/icons/edit.svg"
        }

        return <Icon name={"Password"} size="small" />;
      },
      onClick: async(row: RowTable) => {
        setIsLoading(true);
        setMessageLoading("Cambiando tipo de firma...");

        try {
          const idSolicitud = row.idSolicitud.toString();
          const isFAD = statusFAD.includes(row.idEstatus as Status);

          // Ejecutar el cambio de firma
          if (isFAD) {
            await changeToManualSign(idSolicitud);
          } else {
            await sendToFAD(idSolicitud);
          }

          // Determinar los estados esperados después del cambio
          const expectedStatuses: Status[] = isFAD
            ? [Status.CONTRACT_REVIEW, Status.CONTRACT_VALIDATION_REJECT] // Estados de firma manual
            : statusFAD; // Estados de FAD

          // Realizar polling para verificar el cambio de estado
          setMessageLoading("Verificando cambio de estado...");
          const statusUpdated = await pollForStatusChange(idSolicitud, expectedStatuses);

          if (!statusUpdated) {
            // Si no se actualizó el estado después del polling, recargar toda la lista
            setMessageLoading("Actualizando lista de solicitudes...");
            await getList(agent, appliedFilters);
          }
        } catch (error) {
          console.error(error);
          enqueueSnackbar("Ocurrió un error al cambiar el tipo de firma", { variant: "error" });
        } finally {
          setIsLoading(false);
        }
      },
      disabled: (solicitud: RowTable) => {
        return !isSignatureStatus(solicitud.idEstatus as Status)  || !isMine(solicitud as RowTable);
      }
    },
    {
      label: "Continuar solicitud en Punto Aserta",
      icon: "/assets/icons/arrows.svg",
      onClick: siebelContinueHandler,
      disabled: (solicitud: RowTable) => {
        return !isContinueInPuntoAserta(solicitud.idEstatus as Status) || !isMine(solicitud as RowTable);
      }
    },
    {
      label: "Ver estatus de firmas",
      onClick: (row: RowTable) => {
        const idProcess = row.idSolicitud.toString();
        push(`/sing/fad-status?idProcess=${idProcess}`);
      },
      icon: "/assets/icons/file-question-2.svg",
      disabled: (solicitud: RowTable) => {
        // Habilitar solo cuando está en firma FAD (en-firma)
        return solicitud.idEstatus !== Status.IN_SIGNING || !isMine(solicitud as RowTable);
      }
    },
    {
      label: "Ver historial con operaciones",
      icon: "/assets/icons/eye.svg",
      onClick: viewHistoryHandler,
      disabled: (solicitud: RowTable) => {
        return !Boolean(solicitud.historialOperaciones) || !isMine(solicitud as RowTable);
      }
    },
    {
      label: "Eliminar solicitud",
      onClick: cancelHandler,
      icon: "/assets/icons/trash-01.svg",
      disabled: (solicitud: RowTable) => {
        try {
          if (isValidStatus(solicitud.idEstatus, statusByAction.cancel)) {
            throw new Error("Invalid status");
          }
          /* const insurgentes = solicitud.idSiebelInsurgentes ?? "";
          const aserta = solicitud.idSiebelAserta ?? ""; */
          return !isMine(solicitud as RowTable);

        } catch (error) {
          return true;
        }
      },
    }
  ];

  const handleCancel = async (idSolicitud: string) => {
    try {
      setIsLoading(true);
      setModalProperties((prev) => ({ ...prev, open: false }));
      setMessageLoading("Cancelando solicitud...");

      const response = await cancelComparison({ idProcess: idSolicitud });
      if (!response || response !== "OK") {
        throw new Error("No fue posible cancelar la solicitud");
      }

      await getList(agent, appliedFilters);
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const pushHandler = async (rute: string) => {
    push(rute);
  };

  const closeModal = () => {
    setModalProperties({ open: false, title: "", type: "view", idSolicitud: "" });
  };

  // Estilos
  const stylesBoxSearch = {
    searchContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "16px 24px",
      backgroundColor: "#EAECF0",
      boxShadow: "inset 0px 4px 8px rgba(127, 130, 145, 0.2)",
      width: "100%",
      height: "80px"
    },
    actionsWrapper: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: "16px",
      width: "100%",
      height: "48px"
    },
    inputContainer: {
      display: "flex",
      flex: 1,
    },
    inputWrapper: {
      display: "flex",
      alignItems: "center",
      padding: "12px",
      width: "100%",
      height: "48px",
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        padding: "12px",
        height: "48px",
        fontSize: "16px",
        color: "#667085",
        backgroundColor: "#FFFFFF",
        '& fieldset': {
          border: "1px solid #D0D5DD",
          borderRadius: "8px",
        },
        '&:hover fieldset': {
          borderColor: "#D0D5DD",
        },
        '&.Mui-focused fieldset': {
          borderColor: "#D0D5DD",
        },
      },
      '& .MuiOutlinedInput-input': {
        padding: 0,
        "&::placeholder": {
          fontSize: "16px",
          color: "#667085",
        },
      },
      '& .MuiInputBase-input': {
        border: "none",
        outline: "none",
        fontSize: "16px",
        color: "#667085",
      },
    },
    filterButton: {
      height: "48px",
      width: "109px",
      border: "1px solid #2E3C6F",
      color: "#2E3C6F",
      boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
      '& .MuiButton-endIcon': {
        marginLeft: "8px"
      }
    }
  };

  const boxStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    height: "100%",
    gap: 1,
    alignContent: "center",
  };

  const boxStyleSelect = {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    height: "100%",
    alignContent: "center",
    valign: "center",
    minWidth: "150px",
    flex: "1",
  };

  const contentContainerStyles = {
    width: 'calc(100% + 8px)',
    position: 'relative' as const,
    height: 'calc(100% - 8px)',
  };

  const newClientHandler = () => {
    try {
      setIsLoading(true);
      setMessageLoading("");
      push("/registration/v2");
    } catch (error) {
      console.error("Error en navegación: ", error);
    } finally {      
      setIsLoading(false);
      push("/registration/v2");
    }
  }

  return (
    <UIPrivateLayout>
      <UIRightSidebarLayout
        isLoading={isLoading}
        messageLoading={messageLoading}
        open={false}
        drawerWidth={240}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", padding: "32px", gap: "16px", width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Breadcrumbs options={breadcrumbsOptions} />
            <UIButton
              label="Nuevo cliente"
              color="orange"
              size="full"
              startIcon={<PersonAddIcon />}
              onClick={newClientHandler}
              sx={{ height: "44px", whiteSpace: "nowrap" }}
            />
          </Box>

          <Box sx={{
            border: `1px solid ${colors.grayLM200}`,
            borderRadius: "12px",
            backgroundColor: "white",
            height: "100%",
            overflow: "hidden",
            width: "100%"
          }}>
            <Grid container spacing={{ sm: 2, md: 3 }} sx={{ padding: "20px", alignItems: "center" }}>
              <Grid item xs={12} sm={2.5} md={2.5} lg={isSpecialUser ? 3 : 3.5} xl={isSpecialUser ? 3 : 3.5} sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                '@media (min-width:900px)': { flexDirection: "row" }
              }}>
                <UIText
                  text="Altas en proceso"
                  size="text-lg"
                  color="blue"
                  weight="semibold"
                  sx={{ marginRight: "10px" }}
                />
                <UIChip
                  label={`${filteredData.length} de ${allRequests.length}`}
                  type="primary"
                />
              </Grid>

              <Grid item xs={12} sm={2.5} md={2.3} lg={2.1} xl={2.1} sx={boxStyle}>
                <UIText text="Aseguradoras" size="text-s" color="gray600" weight="medium" />
                <UIRadioButton
                  value={tempFilters.aseguradora}
                  textSize="text-s"
                  options={companies}
                  handleChange={handleCompanyChange}
                />
              </Grid>

              <Grid item xs={12} sm={2.5} md={2.6} lg={2.6} xl={2.6} sx={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-start" }}>
                <Box sx={boxStyleSelect}>
                  <UIText text="Sucursal" size="text-s" color="gray600" weight="medium" />
                  <UISelect
                    value={tempFilters.sucursal}
                    onChange={handleSucursalChange}
                    options={sucursales.map((item) => ({ id: item.id, value: item.value }))}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={2} md={2.6} lg={2.6} xl={2.6} sx={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-start" }}>
                {isRoleSucPromotorOrAgent(session) && (
                  <Box sx={boxStyleSelect}>
                    <UIText text="Agentes" size="text-s" color="gray600" weight="medium" />
                    {(isLoadingAgents || isFirstLoad) ? (
                      <Box sx={contentContainerStyles}>
                        <UILoadingOverlay variant="spinner" />
                      </Box>
                    ) : (
                      <UISelectSearch
                        label=""
                        value={selected?.length === 1 ? selected[0] : ""}
                        options={agents}
                        onChange={(values, ids) => handleAgentChange(values, ids)}
                      />
                    )}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={1.9} lg={isSpecialUser ? 1.7 : 1} xl={isSpecialUser ? 1.7 : 1} sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end"
              }}>
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                  <UIText text="." size="text-s" color="transparent" weight="medium" />
                  <Box sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    gap: "12px",
                    marginTop: "7px",
                  }}>
                    {/* Botón Limpiar solo para usuarios especiales */}
                    {isSpecialUser && hasActiveFilters && (
                      <UIButton
                        color="secondary"
                        label="Limpiar"
                        onClick={handleClearAllFilters}
                      />
                    )}

                    <UIButton
                      color="orange"
                      label="Buscar"
                      onClick={handleSearch}
                      disabled={isSearchDisabled}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={stylesBoxSearch.searchContainer}>
              <Box sx={stylesBoxSearch.actionsWrapper}>
                <Box sx={stylesBoxSearch.inputContainer}>
                  <Paper sx={stylesBoxSearch.inputWrapper}>
                    <InputBase
                      fullWidth
                      sx={stylesBoxSearch.textField}
                      placeholder="Escribe el RFC, nombre o ID para filtrar sobre los resultados"
                      value={tempFilters.searchTerm}
                      onChange={(e) => handleTempFilterChange('searchTerm', e.target.value)}
                    />
                    <IconButton
                      type="button"
                      sx={{ p: '10px' }}
                      aria-label="Clear"
                      onClick={() => handleTempFilterChange('searchTerm', '')}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Paper>
                </Box>

                <UIButton
                  label="Filtros"
                  color="secondary"
                  endIcon={isFilterOpen ? <CloseIcon /> : <FilterListIcon />}
                  onClick={toggleFilterSidebar}
                  sx={stylesBoxSearch.filterButton}
                />
              </Box>
            </Box>

            <Box sx={{
              display: "flex",
              width: "100%",
              position: "relative",
              height: "calc(100% - 100px)",
              flexDirection: "column",
            }}>
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <UITable
                  columns={columnsTable}
                  rows={filteredData}
                  actions={actions}
                />
              </Box>
            </Box>

            {isLoadingMore && (
              <Box sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '12px',
                zIndex: 1000
              }}>
                Cargando más solicitudes...
              </Box>
            )}
          </Box>

          <UIModal
            open={modalProperties.open}
            onClose={closeModal}
            logo={"/assets/icons/face.svg"}
            size={{ height: "auto", width: "" }}
            titleContent={modalProperties.title}
            bodyStyles={{ height: "auto" }}
            body={
              <UIContentModal
                type={modalProperties.type}
                idSolicitud={modalProperties.idSolicitud}
                isFad={modalProperties.isFad}
                onCanceled={handleCancel}
                pushed={pushHandler}
                close={closeModal}
              />
            }
          />
        </Box>

        <FilterSidebar
          filters={tempFilters}
          onApplyFilters={handleFilterChange}
          isOpen={isFilterOpen}
          onClose={toggleFilterSidebar}
        />
      </UIRightSidebarLayout>
    </UIPrivateLayout>
  );
}

const PageWrapper = () => (
  <Suspense fallback={<UILoading />}>
    <Home />
  </Suspense>
);

export default PageWrapper;